import { useCallback, ReactNode } from 'react';
import { BaseAnalyticsCard } from './BaseAnalyticsCard';
import { BaseTable, tableUtils } from './BaseTable';
import { COMMON_VIEW_OPTIONS } from '../config';
import { Calendar } from 'lucide-react';
import { DetailModalProps, AnalyticsQueryResult, ControlOption } from './types';
import { CHART_CONFIG, ANALYTICS_CONFIG } from '../config';
import { formatPeriod } from '../utils/dataUtils';

interface BaseTrendCardProps<TData, TFilters> {
  title: string | ((metric: 'sessions' | 'minutes') => string);
  filters: TFilters;
  dataHook: (filters: TFilters) => AnalyticsQueryResult<TData[]>;

  getDate: (item: TData) => Date | string;
  getMainMetric: (item: TData) => number;
  getSecondaryMetric?: (item: TData) => number;
  getChange?: (item: TData) => number | null;

  height?: number;
  dateFormat?: string;
  mainMetricLabel?: string;
  secondaryMetricLabel?: string;
  emptyMessage?: string;
  metricOptions?: ControlOption<'sessions' | 'minutes'>[];

  ChartComponent: React.ComponentType<{
    data: TData[];
    height: number;
    showSessions?: boolean;
    showMinutes?: boolean;
  }>;

  renderLog?: () => ReactNode;
  viewOptions?: ControlOption<'chart' | 'table' | 'log'>[];
  dropdownViewOptions?: Array<'chart' | 'table'>;
  exportData?: unknown[] | ((activeView: 'chart' | 'table' | 'log') => unknown[] | undefined);
  exportFileName?: string | ((activeView: 'chart' | 'table' | 'log') => string);
  DetailModal?: React.ComponentType<DetailModalProps<TData, TFilters>>;
}

export function BaseTrendCard<TData, TFilters>({
  title,
  filters,
  dataHook,
  getDate,
  getMainMetric,
  getSecondaryMetric,
  getChange,
  height = CHART_CONFIG.DEFAULT_HEIGHT,
  dateFormat = 'month',
  mainMetricLabel = ANALYTICS_CONFIG.METRIC_LABELS.SESSIONS,
  secondaryMetricLabel = ANALYTICS_CONFIG.METRIC_LABELS.MINUTES,
  emptyMessage = 'No trend data available',
  metricOptions,
  ChartComponent,
  renderLog,
  viewOptions = COMMON_VIEW_OPTIONS,
  dropdownViewOptions,
  exportData,
  exportFileName,
  DetailModal,
}: BaseTrendCardProps<TData, TFilters>) {
  const renderChart = useCallback(
    (data: TData[], metric: string, onCardClick?: () => void) => {
      const chart = (
        <ChartComponent
          data={data}
          height={height}
          showSessions={metric === ANALYTICS_CONFIG.METRIC_TYPES.SESSIONS}
          showMinutes={metric === ANALYTICS_CONFIG.METRIC_TYPES.MINUTES}
        />
      );

      if (onCardClick) {
        return (
          <div onClick={onCardClick} className="cursor-pointer">
            {chart}
          </div>
        );
      }

      return chart;
    },
    [ChartComponent, height],
  );

  const renderTable = useCallback(
    (data: TData[], onItemClick?: (item: TData) => void) => {
      const columns = [
        {
          key: 'date',
          label: ANALYTICS_CONFIG.METRIC_LABELS.PERIOD,
          render: (item: TData) => (
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-[var(--anl-text-muted)]" />
              <span className="font-medium text-[var(--anl-text)]">
                {formatPeriod(getDate(item), dateFormat === 'month' ? 'month' : 'date')}
              </span>
            </div>
          ),
          align: 'left' as const,
        },
        tableUtils.metricColumn('main', mainMetricLabel, getMainMetric),
        ...(getSecondaryMetric
          ? [
              tableUtils.metricColumn('secondary', secondaryMetricLabel, getSecondaryMetric, {
                format: 'duration',
              }),
            ]
          : []),
        ...(getChange
          ? [tableUtils.changeColumn('change', ANALYTICS_CONFIG.METRIC_LABELS.CHANGE, getChange)]
          : []),
      ];

      return (
        <BaseTable
          data={data}
          columns={columns}
          onRowClick={onItemClick}
          keyExtractor={item => getDate(item).toString()}
          emptyMessage={emptyMessage}
        />
      );
    },
    [
      getDate,
      getMainMetric,
      getSecondaryMetric,
      getChange,
      dateFormat,
      mainMetricLabel,
      secondaryMetricLabel,
      emptyMessage,
    ],
  );

  return (
    <BaseAnalyticsCard
      title={title}
      filters={filters}
      dataHook={dataHook}
      renderChart={renderChart}
      renderTable={renderTable}
      emptyMessage={emptyMessage}
      metricOptions={metricOptions}
      viewOptions={viewOptions}
      dropdownViewOptions={dropdownViewOptions}
      renderLog={renderLog}
      exportData={exportData}
      exportFileName={exportFileName}
      DetailModal={DetailModal}
      accent={CHART_CONFIG.COLORS.PRIMARY}
    />
  );
}
